import { GetterTree, MutationTree, ActionTree, ActionContext, Module } from 'vuex';
import { RootState } from '@/store/types';
import { ApplicantCustomColor } from '@/api/auth';
import { colorStringToHsl, colorHslToStringWithLuminanceDecrease } from '@/util/color';
import { OsType, getOSType } from '@/util/device';

type State = {
  launchText: string;
  launchGradientTop: string;
  launchGradientBottom: string;
  maintenanceLogo: string;
  maintenanceGradientTop: string;
  maintenanceGradientBottom: string;
  pageHeaderText: string;
  pageHeaderColor: string;
  errorHeaderText: string;
  errorHeaderColor: string;
  buttonFocus: string;
  positiveButtonText: string;
  positiveButtonEnabled: string;
  positiveButtonHover: string;
  positiveButtonActive: string;
  positiveButtonDisabled: string;
  negativeButtonEnabled: string;
  negativeButtonHover: string;
  negativeButtonActive: string;
  shutterButtonText: string;
  shutterButtonEnabled: string;
  shutterButtonHover: string;
  shutterButtonActive: string;
  shutterButtonDisabled: string;
};

function createState(): State {
  const state: State = {} as State;
  clearState(state);
  return state;
}

function clearState(state: State): void {
  const isPC: boolean = getOSType() === OsType.PC;
  state.launchText = isPC ? '#ffffff' : '#777777'; // 青グラデーション時代の色は '#ffffff'
  state.launchGradientTop = isPC ? '#d8d8d8' : '#ffffff'; // 青グラデーション時代の色は '#f3edd9';
  state.launchGradientBottom = isPC ? '#d8d8d8' : '#ffffff'; // 青グラデーション時代の色は #1bb0d1
  state.maintenanceLogo = '#777777'; // 青グラデーション時代の色は '#ffffff'
  state.maintenanceGradientTop = '#f5f5f5'; // 青グラデーション時代の色は '#f3edd9';
  state.maintenanceGradientBottom = '#f5f5f5'; // 青グラデーション時代の色は #1bb0d1
  state.pageHeaderText = '#ffffff';
  state.pageHeaderColor = '#14a8c8';
  state.errorHeaderText = '#333333';
  state.errorHeaderColor = '#dddddd';
  state.buttonFocus = 'tomato';
  state.positiveButtonText = '#ffffff';
  state.positiveButtonEnabled = '#1bb0d1';
  state.positiveButtonHover = '#189eb9';
  state.positiveButtonActive = '#158aa2';
  state.positiveButtonDisabled = '#dddddd';
  state.negativeButtonEnabled = '#0590ae';
  state.negativeButtonHover = '#047c94';
  state.negativeButtonActive = '#03677b';
  state.shutterButtonText = '#00b5d9';
  state.shutterButtonEnabled = '#ffffff';
  state.shutterButtonHover = '#f2f2f2';
  state.shutterButtonActive = '#e5e5e5';
  state.shutterButtonDisabled = '#dddddd';
}

const getters: GetterTree<State, RootState> = {};

const mutations: MutationTree<State> = {
  setColors(state: State, payload: ApplicantCustomColor[]) {
    payload.forEach((applicantCustomColor: ApplicantCustomColor): void => {
      if (applicantCustomColor.key === 'typicalHeaderColor') {
        state.pageHeaderColor = applicantCustomColor.colorCode;
      } else if (applicantCustomColor.key === 'typicalButtonColor') {
        const colorString: string = applicantCustomColor.colorCode;
        const colorHsl: [number, number, number] = colorStringToHsl(colorString);
        state.positiveButtonEnabled = colorString;
        state.positiveButtonHover = colorHslToStringWithLuminanceDecrease(colorHsl, 1);
        state.positiveButtonActive = colorHslToStringWithLuminanceDecrease(colorHsl, 2);
        state.negativeButtonEnabled = colorHslToStringWithLuminanceDecrease(colorHsl, 2);
        state.negativeButtonHover = colorHslToStringWithLuminanceDecrease(colorHsl, 3);
        state.negativeButtonActive = colorHslToStringWithLuminanceDecrease(colorHsl, 4);
        state.shutterButtonText = colorString;
      }
    });
  },

  clearState(state: State) {
    clearState(state);
  }
};

const actions: ActionTree<State, RootState> = {
  clearState(context: ActionContext<State, RootState>): void {
    context.commit('clearState');
  },

  setApplicantCustomColors(context: ActionContext<State, RootState>, payload: ApplicantCustomColor[]): void {
    context.commit('clearState');
    context.commit('setColors', payload);
  }
};

const module: Module<State, RootState> = {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions
};

export default module;
